import {
    createSlice, PayloadAction,
} from '@reduxjs/toolkit';

import {isEnumInstance} from 'shared/utils';
import {getIsAuthenticatedFromStorage} from 'shared/utils/storage';
import {AuthResponseTypes, AUTH_SLICE, AuthErrors} from 'store/slices/auth-slice/auth-slice-constants';
import {
    authorize,
    logout,
    changePassword,
    loadUserInfo,
    getTwoFAConfig,
    resetTokenInfo,
    getAuthProfile,
    checkAuthStatus,
    loadMicroserviceToken,
    get2faCodeValidity,
    check2faCode,
} from 'store/slices/auth-slice/auth-slice-thunks';
import {IAuthError, IAuthSliceState, ETwoFactorAuthCode} from 'store/slices/auth-slice/auth-slice-types';

export const authSlice = createSlice({
    initialState: {
        error: null,
        userName: undefined,
        isAuthenticated: false,
        isPasswordExpired: undefined,
        isPasswordSuccessfullyChanged: undefined,
        userInfo: undefined,
        useTwoFAVerificationFlag: undefined,
        twoFactorAuthTypeCode: undefined,
        microserviceToken: null,
        twoFaCodeValidity: undefined,
    } as IAuthSliceState,
    name: AUTH_SLICE,
    reducers: {
        setPasswordExpired(state, {payload}: PayloadAction<boolean>) {
            state.isPasswordExpired = payload;
        },
        setError(state, {payload}: PayloadAction<IAuthError>) {
            state.error = payload;
        },
        resetError(state) {
            state.error = null;
        },
        resetUserInfo(state) {
            state.userInfo = undefined;
        },
        resetPasswordExpired(state) {
            state.isPasswordExpired = undefined;
        },
        resetToken(state) {
            resetTokenInfo();
            state.isAuthenticated = false;
        },
        resetMicroserviceToken(state) {
            state.microserviceToken = null;
        },
    },
    extraReducers: builder => {
        builder.addCase(authorize.fulfilled, (state, action) => {
            const {isPortal2faAuth} = action.payload;

            if (!isPortal2faAuth) {
                state.isAuthenticated = true;
            }
            state.isPasswordSuccessfullyChanged = undefined;
        });
        builder.addCase(authorize.rejected, (state, action) => {
            const {
                userName,
                code,
                type,
                message,
            } = action.payload ?? {};

            if (code === 401) {
                if (type === AuthResponseTypes.PASSWORD_EXPIRED) {
                    state.userName = userName;
                    state.isPasswordExpired = true;
                }
            }

            state.error = {message: message || action.error?.message || AuthErrors.DEFAULT_ERROR_MESSAGE};
        });

        builder.addCase(check2faCode.fulfilled, (state) => {
            state.isAuthenticated = getIsAuthenticatedFromStorage();
        });
        builder.addCase(check2faCode.rejected, (state, action) => {
            const {code, message, type} = action.payload ?? {};
         
            if (code === 401) {
                if (type === AuthResponseTypes.PASSWORD_EXPIRED) {
                    state.isPasswordExpired = true;
                }
            }

            state.error = {message: message || action.error?.message || AuthErrors.DEFAULT_ERROR_MESSAGE};
        });
        builder.addCase(logout.fulfilled, () => authSlice.getInitialState());
        builder.addCase(loadUserInfo.fulfilled, (state, action) => {
            state.userInfo = action.payload;
        });
        builder.addCase(changePassword.fulfilled, state => {
            state.error = null;
            state.isPasswordExpired = undefined;
            state.isPasswordSuccessfullyChanged = true;
        });
        builder.addCase(changePassword.rejected, (state, action) => {
            state.error = {message: action.payload};
        });
        builder.addCase(getTwoFAConfig.fulfilled, (state, action) => {
            const isTwoFactorCode = isEnumInstance(action.payload, ETwoFactorAuthCode);
            state.useTwoFAVerificationFlag = isTwoFactorCode;

            if (isTwoFactorCode) {
                state.twoFactorAuthTypeCode = action.payload as ETwoFactorAuthCode;
            }
        });
        builder.addCase(getTwoFAConfig.rejected, (state, action) => {
            state.error = {message: action.payload};
        });
        builder.addCase(getAuthProfile.fulfilled, (state, action) => {
            state.profile = action.payload;
        });
        builder.addCase(get2faCodeValidity.fulfilled, (state, action) => {
            state.twoFaCodeValidity = action.payload;
        });
        builder.addCase(getAuthProfile.rejected, (state, action) => {
            state.error = {message: action.payload};
        });
        builder.addCase(checkAuthStatus.fulfilled, (state, action) => {
            state.isAuthenticated = !!action.payload;
            state.error = null;
        });
        builder.addCase(loadMicroserviceToken.fulfilled, (state, action) => {
            state.microserviceToken = action.payload;
        });
    },
});

export const {
    reducer: authSliceReducer,
    actions: authSliceActions,
} = authSlice;
